import { isNative } from '../native';
import { SITE } from './Agreement';
import { useState } from 'react';
import { c } from '../theme';
import { Body, Headline, MicroLabel, PrimaryButton, Shell, Wordmark } from '../components/Brand';
import { Menu } from '../components/Menu';
import { api, ApiError, type ReportSummary, type User } from '../api';
import { useAuth } from '../auth';

export function Account({ user, reports }: { user: User | null; reports: ReportSummary[] }) {
  const { signOut } = useAuth();
  // Deletion takes two taps: the first explains what goes, the second does it.
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.deleteAccount();
      await signOut();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete the account. Please try again.');
      setDeleting(false);
    }
  };

  const rows: { label: string; value: string }[] = [
    { label: 'NAME', value: user?.name ?? '—' },
    { label: 'EMAIL', value: user?.email ?? '—' },
    { label: 'REPORTS', value: `${reports.length}` },
    { label: 'DATA SOURCES', value: 'Licensed property data and the national census' },
  ];

  return (
    <Shell>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Wordmark />
        <Menu current="account" user={user} reportCount={reports.length} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 56 }}>
        <MicroLabel>ACCOUNT</MicroLabel>
        <Headline>
          Your
          <br />
          details<span style={{ color: c.red }}>.</span>
        </Headline>
        <Body>Billing and API keys are not wired up in this build.</Body>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', paddingTop: 36 }}>
        {rows.map((r) => (
          <div
            key={r.label}
            style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '16px 0', borderTop: `1px solid ${c.hairlineList}` }}
          >
            <div style={{ fontSize: 10, letterSpacing: '0.14em', fontWeight: 600, color: c.ink5 }}>{r.label}</div>
            <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>{r.value}</div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1 }} />

      <PrimaryButton onClick={() => void signOut()} style={{ marginTop: 36 }}>
        sign out
      </PrimaryButton>

      {/* Legal pages on readybuy.au (the app stores require the privacy policy to be reachable in the app) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 18px', paddingTop: 22 }}>
        {[['/privacy/', 'PRIVACY POLICY'], ['/terms/', 'TERMS'], ['/disclaimer/', 'DISCLAIMER'], ['/support/', 'SUPPORT']].map(([path, label]) => (
          <a key={path} href={SITE + path} target="_blank" rel="noreferrer" style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 600, color: c.ink5, textDecoration: 'none' }}>
            {label}
          </a>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 22, textAlign: 'center' }}>
        {confirming && (
          <div style={{ fontSize: 12, lineHeight: 1.5, color: c.ink6 }}>
            This permanently deletes your account and all your reports. It cannot be undone.{isNative ? '' : ' Your signed testing agreement is kept as a legal record.'}
          </div>
        )}
        {error && <div style={{ fontSize: 12, color: c.red }}>{error}</div>}
        <button
          type="button"
          disabled={deleting}
          onClick={() => (confirming ? void remove() : setConfirming(true))}
          style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 700, color: c.red, padding: '10px 0' }}
        >
          {deleting ? 'DELETING…' : confirming ? 'YES, DELETE MY ACCOUNT' : 'DELETE ACCOUNT'}
        </button>
        {confirming && !deleting && (
          <button type="button" onClick={() => setConfirming(false)} style={{ fontSize: 11, letterSpacing: '0.14em', fontWeight: 600, color: c.ink5 }}>
            CANCEL
          </button>
        )}
      </div>
    </Shell>
  );
}
